import {
  BaseTextField,
  type BaseTextFieldProps,
} from '@ui/atoms/base-text-field/base-text-field'
import clsx from 'clsx/lite'
import React, { useState } from 'react'

export type TextFieldProps = {
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  value?: string | readonly string[] | number | undefined
  defaultValue?: string | number | readonly string[] | undefined
} & Omit<BaseTextFieldProps, 'input' | 'hasFocus' | 'shrink'>

export const TextField: React.FC<TextFieldProps> = ({
  autoFocus = false,
  disabled = false,
  endAdornments,
  fullWidth = false,
  helper,
  inputProps,
  label,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  required = false,
  size = 'normal',
  type = 'text',
  validation,
  value,
  defaultValue,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(autoFocus)
  const [shrink, setShrink] = useState(Boolean(defaultValue))

  const handleBlur = (
    event:
      | React.FocusEvent<HTMLInputElement>
      | React.FocusEvent<HTMLTextAreaElement>
  ) => {
    setIsFocused(false)
    setShrink(Boolean(event.target.value))
    onBlur?.(event)
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange?.(event)
  }

  const handleFocus = (
    event:
      | React.FocusEvent<HTMLInputElement>
      | React.FocusEvent<HTMLTextAreaElement>
  ) => {
    setIsFocused(true)
    setShrink(true)
    onFocus?.(event)
  }

  return (
    <BaseTextField
      defaultValue={defaultValue}
      disabled={disabled}
      endAdornments={endAdornments}
      fullWidth={fullWidth}
      hasFocus={isFocused}
      helper={helper}
      label={label}
      name={name}
      placeholder={placeholder}
      shrink={shrink}
      size={size}
      type={type}
      validation={validation}
      value={value}
      input={
        <input
          defaultValue={defaultValue}
          disabled={disabled}
          id={name}
          name={name}
          placeholder={placeholder}
          required={required}
          type={type}
          value={value}
          className={clsx(
            'relative z-[1] block h-full w-full appearance-none pb-[11px] pl-[11px] text-100 font-medium',
            'placeholder:font-normal',
            'focus-visible:outline-none',
            size === 'normal' ? 'pt-8' : 'pt-[11px]',
            endAdornments ? 'pr-11' : 'pr-[11px]'
          )}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          {...inputProps}
        />
      }
      {...props}
    />
  )
}
